/**
 * Shared session-finalization helper.
 * Consolidates XP, streak, achievement, task-minute, and session-save logic
 * that was previously duplicated across Focus, MiniPlayer, and FlowRunner.
 */
import useSessionStore from '../stores/useSessionStore';
import useGamificationStore from '../stores/useGamificationStore';
import useTaskStore from '../stores/useTaskStore';
import { XP_PER_MINUTE, COMPLETION_BONUS, STREAK_MULTIPLIER, MAX_STREAK_MULTIPLIER } from './constants';
import { localDateStr } from './dateUtils';

/**
 * @param {Object} opts
 * @param {number}  opts.duration      - Focus duration in seconds
 * @param {boolean} opts.completed     - Whether session completed naturally (vs stopped early)
 * @param {string}  opts.mode          - 'freestyle' | 'flow'
 * @param {Object}  opts.sessionData   - Full session record fields (identityId, projectId, etc.)
 * @param {string[]} opts.taskIds      - Task IDs to distribute minutes across
 */
export function finalizeSession({ duration, completed, mode, sessionData, taskIds = [] }) {
    const profile = useGamificationStore.getState().profile;
    const addXP = useGamificationStore.getState().addXP;
    const updateStreak = useGamificationStore.getState().updateStreak;
    const checkAchievements = useGamificationStore.getState().checkAchievements;
    const getLevel = useGamificationStore.getState().getLevel;
    const addFocusMinutesToWeeklyGoal = useGamificationStore.getState().addFocusMinutesToWeeklyGoal;
    const syncSessionAward = useGamificationStore.getState().syncSessionAward;
    const addSession = useSessionStore.getState().addSession;
    const addActualMinutes = useTaskStore.getState().addActualMinutes;

    const minutes = duration / 60;
    // Computed once and reused for BOTH the local streak update and the server sync below,
    // so they can never disagree about which local day this session belongs to.
    const nowIso = new Date().toISOString();

    // 1. XP calculation with streak multiplier
    const streakMultiplier = Math.min(
        1 + (profile.currentStreak || 0) * STREAK_MULTIPLIER,
        MAX_STREAK_MULTIPLIER
    );
    const baseXP = Math.floor(minutes * XP_PER_MINUTE);
    const bonus = completed ? COMPLETION_BONUS : 0;
    const xpEarned = Math.floor((baseXP + bonus) * streakMultiplier);

    // 2. Save session
    const session = addSession({
        ...sessionData,
        duration,
        completed,
        xpEarned,
        mode,
    });

    // 3. Award XP
    addXP(xpEarned);

    // 4. Update streak
    updateStreak(nowIso);

    // 4b. Weekly focus-minutes goal (guilt-free: increments this week, auto-resets next week)
    addFocusMinutesToWeeklyGoal(minutes, nowIso);

    // 5. Check achievements with REAL computed level
    const allSessions = useSessionStore.getState().sessions;
    const updatedProfile = useGamificationStore.getState().profile;
    const totalSessions = allSessions.length;
    const totalFocusTime = allSessions.reduce((s, sess) => s + (sess.duration || 0), 0);
    checkAchievements({
        totalSessions,
        totalFocusTime,
        currentStreak: updatedProfile.currentStreak,
        level: getLevel(),
    });

    // 5b. Server reconciliation (durable path, Phase P2) — fire-and-forget, NEVER awaited
    // and NEVER allowed to affect what the user already sees. Steps 1-5 above are the
    // optimistic local numbers the UI shows instantly; this only reconciles the
    // authoritative server totals in once the round trip completes, and silently no-ops
    // on any failure (offline, App Check, credits, anything else) — see
    // useGamificationStore.syncSessionAward's own doc for the full contract. `stats` carries
    // every achievement counter the server can't derive itself that IS actually available at
    // this call site (FINDING 2, 2026-08-30): totalSessions/totalFocusTime (from
    // useSessionStore, computed just above) and totalCircuitsCompleted (this same
    // useGamificationStore profile's own awardedCircuitRuns — see grantCircuitCompletion).
    // totalVoiceDumps is deliberately NOT included: voice-dump completion never routes
    // through finalizeSession/syncSessionAward at all — it lives entirely in
    // useVoiceDumpStore, which calls checkAchievements locally and never calls the
    // awardEvent callable — so there is no real count of it available at this call site.
    // Inventing one here would be worse than omitting it; the 'first-dump'/'10-dumps'
    // achievements accordingly still only unlock locally until that path gets its own
    // wiring (separate work, outside a session-completion call site). currentStreak/level/
    // totalXP are deliberately left out of `stats` so the server's own authoritative
    // computation of those is never clobbered by a client-supplied value. The extra .catch
    // is defensive belt-and-suspenders — syncSessionAward already swallows every failure
    // internally and never rejects.
    //
    // `baseline` is the profile snapshot captured at the very top of this function,
    // BEFORE steps 3/4 applied this session's own optimistic addXP/updateStreak — i.e.
    // this account's history up to (not including) the session just finalized. The
    // server (functions/src/gamification/awardEvent.js) seeds a first-time gamification
    // document from exactly this — or SELF-HEALS one that already exists, taking the higher
    // of what it holds and what this baseline offers (see awardEvent.js's SELF-HEALING
    // BASELINE ADOPTION note) — and then applies its OWN computation of this same session's
    // award on top, so an existing user's sync lands on top of their real history instead of
    // overwriting it with one session's XP. Using `profile` (pre-session) rather than
    // `updatedProfile` (post-session) here is deliberate: seeding/healing from a total that
    // already includes this session would double count it the moment the server adds its
    // own award for the same event on top.
    const totalCircuitsCompleted = (profile.awardedCircuitRuns || []).length;
    syncSessionAward({
        eventId: session.id,
        minutes,
        completed,
        dayLocal: localDateStr(nowIso),
        stats: { totalSessions, totalFocusTime, totalCircuitsCompleted },
        baseline: {
            totalXP: profile.totalXP,
            coins: profile.coins,
            currentStreak: profile.currentStreak,
            longestStreak: profile.longestStreak,
            lastSessionDate: profile.lastSessionDate,
        },
    }).catch(() => {});

    // 6. Distribute actual minutes across tasks
    if (taskIds.length > 0 && minutes > 0) {
        const perTask = Math.round(minutes / taskIds.length);
        taskIds.forEach(tid => {
            if (tid) addActualMinutes(tid, perTask);
        });
    }

    return { xpEarned, session };
}

/**
 * Shared "stop freestyle session" utility.
 * Calculates elapsed wall-clock duration, saves the session, and stops the timer.
 * Used by both MiniPlayer and useFreestyleRuntime to avoid duplicated stop logic.
 *
 * @param {Object} timer     - useTimerStore state (needs sessionStartTime, focusDuration, sessionMeta, stopTimer)
 * @param {Array}  projects  - Array of project objects (for resolving project name)
 * @param {Array}  allTasks  - Array of task objects (for resolving task title)
 */
export function stopFreestyleSession(timer, projects, allTasks) {
    let elapsed = 0;
    if (timer.sessionStartTime) {
        const startMs = new Date(timer.sessionStartTime).getTime();
        elapsed = Math.floor((Date.now() - startMs) / 1000);
    }
    elapsed = Math.min(elapsed, timer.focusDuration);

    const meta = timer.sessionMeta || {};
    const proj = projects.find(p => p.id === meta.projectId);
    const task = allTasks.find(t => t.id === meta.taskId);
    const taskIds = meta.taskIds || (meta.taskId ? [meta.taskId] : []);

    finalizeSession({
        duration: elapsed,
        completed: false,
        mode: 'freestyle',
        taskIds,
        sessionData: {
            identityId: meta.identityId || null,
            startTime: timer.sessionStartTime,
            endTime: new Date().toISOString(),
            timerType: 'custom',
            tags: meta.tags || [],
            projectId: meta.projectId || null,
            taskId: meta.taskId || null,
            taskIds,
            project: proj?.name || '',
            task: task?.title || '',
            notes: '',
            reflectionNotes: '',
        },
    });

    timer.stopTimer();
}
